import React, { ReactNode, useContext, useEffect, useId, useMemo, useState } from "react";
import { pickProps, sleep } from "simple-utils";
import { ReactTooltip } from "tooltip";
import { Bot, exportContacts, ExportEvent, getExportInfo, Tag, useBotEvents } from "../bot-loaders";
import { AudienceMenu } from "../campaign/CampaignForm";
import { Alert } from "../components/Alert";
import { Modal } from "../components/Modal";
import { ValidateContactButton } from "./contact-validation";
import {Buffer} from 'buffer';
import { formatDuration, useETA } from "../hooks/eta-hook";
import { ProgressBar } from "../components/ProgressBar";
import { TagMenu } from "./tag-menu";
import { Loading } from "../components/Loading";
import { AudienceEvent, logBotEvent } from "../ga/ga-event-logger";


type Destination = 'file' | 'audience' | 'new-audience';
type ContactSource = 'chats' | 'contacts';

type ExportContactState = {
    source: ContactSource
    destination: Destination,
    state: "prepare" | "setup" | "requesting" | "progress" | "success"
    tag?: string
    tags?: Tag[]

    audience?: number
    audienceName?: string
    error?: string
    csvOutput?: string 
    token?: string
}

function toAudienceActionEvent(a:Destination):AudienceEvent{
    switch(a){
        case "audience": return "update_audience";
        case "file": return "create_file";
        case "new-audience": return "create_audience"
    }
    
}

function DestinationOption({selected, onChange, label, value}: {selected: boolean, onChange:(e: Destination) => void, label: ReactNode, value: Destination}){
    const id = useId()
    return <div className="form-check mb-1">
        <input className="form-check-input" checked={selected} type="radio" name="speed" id={id} value={value} onChange={(e) => {if(e.target.checked) onChange(value)}} />
        <label className="form-check-label" htmlFor={id}>
            {label} 
        </label>
</div>

}

export function DestinationMenu({value, onChange}:{value: Destination | null, onChange:(e: Destination) => void }){
    return <>
        <div className='mb-2'>Escolha o destino:</div>
        <DestinationOption label={<>Criar Novo Público</>} value="new-audience" onChange={onChange} selected={value == 'new-audience'} />
        <DestinationOption label={<>Importar em Público Existente</>} value="audience" onChange={onChange} selected={value == 'audience'} />
        <DestinationOption label={<>Baixar Arquivo</>} value="file" onChange={onChange} selected={value == 'file'} />
    </>
}

function AudienceNamePicker({name, onChange}:{name:string, onChange:(e:string)=>void}){
    return <>
        <div className='mt-3 mb-2'>Escolha o nome do público a ser criado:</div>
        <input className="form-control" value={name} onChange={(e) => onChange(e.target.value)} />
    </>
}

function ExportRequestingFeedback({state, setState}:{state: ExportContactState, setState:(s:ExportContactState)=>void}){
    const [event, setEvent] = useState<ExportEvent>({phase: "starting"});
    useEffect(() => {
        if(!state.token) return;

        function onEvent(e:ExportEvent){
            setEvent(e);
        }

        return useBotEvents(state.token, "export-contacts", onEvent, () => setState({...state, state: "setup", error: "Erro Desconhecido"}));
    }, [state.token]);

    useEffect(() => {
        if(event.done){
            if(event.errorMessage)
               return setState({...state, state: "setup", error:event.errorMessage, })
            if(event.file)    
               return setState({...state, state: "success", csvOutput: event.file})
            return setState({...state, state: "success"});
        }
    }, [event])

    const {estimatedTime} = useETA({currentItem: event.current ?? 0, totalItems: event.total ?? 0})

    return <div className="my-4 text-center">
            <i className="fa fa-circle-notch fa-spin me-2"></i> 
            {state.destination == 'file' ? 'Gerando o arquivo.' : 'Processando a requisição.'}
            
            {event.current > 0 &&
                <>
                Por favor aguarde.<br /><span className="text-info">(Tempo estimado: {formatDuration(estimatedTime / 1000)})</span>
                <div className="mt-4 mb-2">
                    {event.phase == 'saving' ? 'Salvando Contatos no destino: ' : 'Lendo contatos do WhatsApp: '} {event.current} / {event.total}
                </div>
                <ProgressBar done={event.current ?? 0} total={event.total ?? 1} />
                </>
            }
    </div>
}

function ExportResultFeedback({state, bot}:{bot: Bot, state: ExportContactState}){
    const file = useMemo(()=>{
        if(state.destination == 'file')
            return Buffer.from(state.csvOutput).toString("base64");
        return null;
    }, [state.csvOutput]);

    if(state.destination == 'audience')
        return <div>O público foi atualizado com sucesso.</div>
    
    if(state.destination == 'new-audience')
        return <div>O novo público foi criado com sucesso.</div>

    useEffect(()=>{
       logBotEvent(bot, toAudienceActionEvent(state.destination));
    }, [])    

    return <div className="">
        <p>O arquivo foi criado com sucesso.</p>
        <div>
            <a download={`contatos.csv`} href={`data:text/csv;charset=utf-8;base64,${file}`} className='btn btn-info me-2 mb-2'><i className='fa fa-save me-2'></i>Salvar Arquivo</a>
        </div>
    </div>
}



function ConctactExportingModal({bot}:{bot: Bot}){
    const [state, setState] = useState<ExportContactState>({destination: "new-audience", source: "chats", state: "prepare"});

    function validate(){
        if(state.destination == 'audience' && !state.audience)
            throw new Error('Escolha o público de destino');
        if(state.destination == 'new-audience' && !state.audienceName?.trim())
            throw new Error('Escolha o nome do público de destino');
    }

    async function doRequest(){
        try {
            validate();
            setState({...state, state: "requesting"});
            var result = await exportContacts(bot, pickProps(state, ["audience", "audienceName", "destination", "source", "tag"]));
            setState({...state, state: "progress", token: result.eventsToken});
        }catch(e){
            setState({...state, state: "setup", error: e.message ?? e});
        }
    }

    useEffect(()=>{
        (async function(){
            var a = await getExportInfo(bot) as {token: string, tags?: Tag[]};
            if(a.token){
                setState({...state, ...a, state: "progress"})
                return;
            } 

            setState({...state, tags: a?.tags, state: "setup"})
        })()
    }, []);

    if(state.state == 'requesting' || state.state == 'progress')
        return <ExportRequestingFeedback state={state} setState={setState} />

    if(state.state == 'success')
        return <ExportResultFeedback bot={bot} state={state} />
    
    if(state.state == 'prepare')   {
        return <Loading label="Carregando Informações..." className="my-5" />
    }


    return <>
        {/*<SourceMenu value={state.source} onChange={(e)=>setState({...state, source: e})} />*/}
        <TagMenu tag={state.tag} tags={state.tags} setTag={(e) => setState({...state, tag: e})} />
        
        <DestinationMenu value={state.destination} onChange={(e)=>setState({...state, destination: e, audienceName: ""})} />
        
        {state.destination == 'audience' && <>
            <div className='mt-3 mb-2'>Selecione o público:</div>
            <AudienceMenu value={state.audience} onChange={(e, label) => setState({...state, audience: e, audienceName: label})} />
        </>}
        {state.destination == 'new-audience' && <AudienceNamePicker name={state.audienceName || ""} onChange={(e) => setState({...state, audienceName: e})}  />}

        {state.error && <Alert content={state.error} onDismiss={() => setState({...state, error: undefined})} />}

        <div className="text-end mt-3">
            <button className="btn btn-primary" onClick={doRequest}>Exportar</button>
        </div>
    </>
}


export function ContactExportingButton({bot}:{bot: Bot}){
    const [open, setOpen] = useState(false);
    return <>
        {open && <Modal title="Exportar Contatos" onHide={() => setOpen(false)} backdrop={'static'}><ConctactExportingModal bot={bot}/></Modal>}
        <button className='btn btn-info mb-2 me-2' onClick={() => setOpen(true)}><i className="fa fa-download me-2"></i> Exportar Contatos</button>
    </>
}


function SourceOption({selected, onChange, label, value}: {selected: boolean, onChange:(e: ContactSource) => void, label: ReactNode, value: ContactSource}){
    const id = useId()
    return <div className="form-check mb-1">
        <input className="form-check-input" checked={selected} type="radio" name="source" id={id} value={value} onChange={(e) => {if(e.target.checked) onChange(value)}} />
        <label className="form-check-label" htmlFor={id}>
            {label} 
        </label>
</div>

}

export function SourceMenu({value, onChange}:{value: ContactSource | null, onChange:(e: ContactSource) => void }){
    return <>
        <div className='mb-2'>Escolha a fonte dos dados:</div>
        <SourceOption label={<>Conversas <span className="opacity-50">(Recomendado)</span></>} value="chats" onChange={onChange} selected={value == 'chats'} />
        <SourceOption label={<>Contatos da Agenda</>} value="contacts" onChange={onChange} selected={value == 'contacts'} />
       
        {
            value == 'contacts' &&
            <div className="alert alert-warning my-2">
                <i className="fa fa-exclamation-triangle me-2"></i> Cuidado ao fazer disparos para números que não iniciaram conversas com você aumenta as chances de ser bloqueado.
            </div>
        }
    </>
}